// Hero and item metadata (id -> display name) with shared readiness.
//
// Before this module, `utils.ready` only covered hero names, item names had no
// readiness at all, and a failed request left `ready` pending forever. Bulk
// normalization did not wait for either, so a fast match response could bake
// "Unknown" hero names or missing item names into the cache.
//
// Here each piece (heroes, items) is loaded independently with bounded retries,
// `ensureMetadata()` never rejects and never hangs: it settles with a status
// object once both pieces have either loaded or exhausted their retries. A
// later call re-requests only the pieces that failed, which makes it safe to
// call from every consumer and from a retry button.
import api_communication from '@/api_communication'
import { startTimer } from '@/service/performance'

export const UNKNOWN_HERO_NAME = 'Unknown'

const formatHeroName = (hero) => {
  const name = String(hero ?? '')
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase()
}

const defaultSleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * @param {object} deps
 * @param {() => Promise<string[]>} deps.loadHeroes resolves to the API hero name list (index = hero id)
 * @param {() => Promise<Record<string, string>>} deps.loadItems resolves to { [itemId]: "Name.png" }
 * @param {number} [deps.retries] extra attempts after the first failure (default 2)
 * @param {number} [deps.retryDelayMs] base back-off delay, doubled per attempt (default 400)
 * @param {(ms: number) => Promise<void>} [deps.sleep] injectable for tests
 */
export function createMetadataStore(deps) {
  const retries = deps.retries ?? 2
  const retryDelayMs = deps.retryDelayMs ?? 400
  const sleep = deps.sleep || defaultSleep

  // Shared, stable identities: `utils.heroesNames` / `utils.itemNames` are
  // read directly by views, so these must be filled in place, never replaced.
  const heroesNames = []
  const itemNames = new Map()

  const state = {
    heroes: { loaded: false, loading: false, error: null, attempts: 0 },
    items: { loaded: false, loading: false, error: null, attempts: 0 },
  }

  const inflight = { heroes: null, items: null }

  const apply = {
    heroes(data) {
      const list = Array.isArray(data) ? data : []
      heroesNames.length = 0
      for (const hero of list) heroesNames.push(formatHeroName(hero))
    },
    items(data) {
      itemNames.clear()
      if (!data || typeof data !== 'object') return
      for (const key in data) {
        itemNames.set(Number(key), String(data[key]).replace('.png', ''))
      }
    },
  }

  const loaders = {
    heroes: deps.loadHeroes,
    items: deps.loadItems,
  }

  const loadPiece = async (piece) => {
    const status = state[piece]
    status.loading = true
    status.error = null

    for (let attempt = 0; ; attempt++) {
      const stop = startTimer(`metadata:${piece}`)
      status.attempts++
      try {
        const data = await loaders[piece]()
        apply[piece](data)
        status.loaded = true
        status.error = null
        stop({ attempt, ok: true })
        return true
      } catch (err) {
        stop({ attempt, ok: false })
        status.error = err
        if (attempt >= retries) {
          console.error(`Failed to load ${piece} metadata after ${attempt + 1} attempt(s):`, err)
          return false
        }
        await sleep(retryDelayMs * 2 ** attempt)
      }
    }
  }

  const ensurePiece = (piece) => {
    const status = state[piece]
    if (status.loaded) return Promise.resolve(true)
    if (inflight[piece]) return inflight[piece]

    const promise = loadPiece(piece).finally(() => {
      status.loading = false
      inflight[piece] = null
    })
    inflight[piece] = promise
    return promise
  }

  /**
   * Resolves (never rejects) with `{ ok, heroes, items }` once both pieces
   * have settled. Loaded pieces are not re-requested; failed pieces are.
   */
  const ensureMetadata = () =>
    Promise.all([ensurePiece('heroes'), ensurePiece('items')]).then(([heroesOk, itemsOk]) => ({
      ok: heroesOk && itemsOk,
      heroes: state.heroes,
      items: state.items,
    }))

  const isReady = () => state.heroes.loaded && state.items.loaded

  const getHeroName = (heroId) => heroesNames[heroId] || UNKNOWN_HERO_NAME
  const getHeroIdByName = (heroName) => heroesNames.indexOf(heroName)
  const getItemName = (itemId) => itemNames.get(itemId)

  return {
    heroesNames,
    itemNames,
    state,
    ensureMetadata,
    isReady,
    getHeroName,
    getHeroIdByName,
    getItemName,
  }
}

const metadata = createMetadataStore({
  loadHeroes: () => api_communication.getHeroesNames(),
  loadItems: () => api_communication.getItems(),
})

export const { heroesNames, itemNames, ensureMetadata, isReady, getHeroName, getHeroIdByName, getItemName } = metadata
export const metadataState = metadata.state

export default metadata
